import { Edit3 } from 'lucide-react';
import { AuthButton } from '@/components/ui';
import type { InventoryDetailItem } from '../types/inventoryDetail.types';

interface InventoryDetailStockBreakdownProps {
  item: InventoryDetailItem;
  warehouseStock: { warehouse_name: string; stock: number }[];
  canUpdate: boolean;
}

export function InventoryDetailStockBreakdown({ item, warehouseStock, canUpdate }: InventoryDetailStockBreakdownProps) {
  const warehouses = warehouseStock.length > 0 
    ? warehouseStock.map(ws => ({ name: ws.warehouse_name, stock: ws.stock, unit: item.unit }))
    : [{ name: 'Chưa có dữ liệu kho', stock: 0, unit: item.unit }];

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 flex flex-col h-full shadow-sm">
      <h3 className="sm:text-sm text-xs font-semibold text-slate-900 mb-4">Tồn kho theo kho</h3>
      
      <div className="flex-1 overflow-auto">
        <table className="w-full sm:text-sm text-xs">
          <thead>
            <tr className="text-slate-400 border-b border-slate-50">
              <th className="text-left pb-2 tracking-wider font-normal">Kho</th>
              <th className="text-right pb-2 tracking-wider font-normal">Tồn kho</th>
              <th className="text-right pb-2 tracking-wider font-normal">Đơn vị</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {warehouses.map((w, i) => (
              <tr key={i} className="group hover:bg-slate-50/50 transition-colors">
                <td className="py-3 text-slate-600">{w.name}</td>
                <td className="py-3 text-right text-slate-900 font-medium">{w.stock.toLocaleString('vi-VN')}</td>
                <td className="py-3 text-right text-slate-400">{w.unit}</td>
              </tr>
            ))}
            <tr className="bg-slate-50/50">
              <td className="py-3 px-2 text-slate-900 font-semibold">Tổng cộng</td>
              <td className="py-3 text-right text-emerald-600 font-bold">{item.current_stock.toLocaleString('vi-VN')}</td>
              <td className="py-3 text-right text-slate-900 font-medium">{item.unit}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-6 pt-4 border-t border-slate-100 space-y-4">
        <div className="flex items-center justify-between group">
          <span className="sm:text-sm text-xs text-slate-500">Tồn kho tối thiểu</span>
          <div className="flex items-center gap-2">
            <span className="sm:text-sm text-xs text-slate-900 font-medium">{item.minimum_stock?.toLocaleString('vi-VN') || '0'} {item.unit}</span>
            <AuthButton 
              hasPermission={canUpdate}
              unauthorizedMessage="Bạn không có quyền chỉnh sửa định mức tồn kho."
              className="p-1 rounded hover:bg-slate-50 text-slate-300 hover:text-emerald-600 transition-all bg-transparent border-none"
            >
              <Edit3 className="h-3 w-3" />
            </AuthButton>
          </div>
        </div>
        <div className="flex items-center justify-between group">
          <span className="sm:text-sm text-xs text-slate-500">Tồn kho tối đa</span>
          <div className="flex items-center gap-2">
            <span className="sm:text-sm text-xs text-slate-900 font-medium">{(item.minimum_stock * 6 || 300).toLocaleString('vi-VN')} {item.unit}</span>
            <AuthButton 
              hasPermission={canUpdate}
              unauthorizedMessage="Bạn không có quyền chỉnh sửa định mức tồn kho."
              className="p-1 rounded hover:bg-slate-50 text-slate-300 hover:text-emerald-600 transition-all bg-transparent border-none"
            >
              <Edit3 className="h-3 w-3" />
            </AuthButton>
          </div>
        </div>
      </div>
    </div>
  );
}
